/** @jsxImportSource @emotion/react */

import * as React from 'react'
import styled from '@emotion/styled'
import { Primary, mixins, spacing } from '@sphinx/components-styles'
import { CaretSortUpIcon, CaretSortDownIcon } from '../icons'
import { jsx, css } from '@emotion/react'
import { Sort } from '../../hooks/useSort'
import {
  Accordion,
  AccordionHeader,
  AccordionDetails,
  AccordionProps,
  AccordionContext,
} from '../accordion/accordion'

const disabledStyle = `
  background-color: ${Primary.navy[5]};
  color: ${Primary.navy[20]};
  pointer-events: none;
`

const TableBorderColor = '#e5e5e5'

export const TableHeaderContent = styled.th<{ withoutBorder?: boolean }>`
  ${mixins.body}
  font-weight: bold;
  box-sizing: border-box;
  box-shadow: inset -1px -1px 0px ${TableBorderColor};
  padding: 0 10px;
  text-align: left;
  min-width: 0;
  ${({ withoutBorder }) =>
    withoutBorder &&
    css`
      box-shadow: inset 0 -1px 0px ${TableBorderColor};
    `}
`

export const TableRow = styled.tr<{ disabled?: boolean }>`
  height: ${spacing.m};

  ${({ disabled }) => disabled && disabledStyle}
`

export const TableHeader = styled.thead`
  background-color: ${Primary.navy[5]};
`
export const TableBody = styled.tbody`
  ${TableRow}:hover {
    background-color: ${Primary.orange[10]};
  }
`
export const TableFooter = styled.tfoot``

export const TableCell = styled.td`
  ${mixins.body}
  padding: 0 10px;
  box-shadow: inset 0 -1px 0px ${TableBorderColor};

  & > label {
    padding: 0;
  }
`

// `height: 1px` as a workaround solution for post-release bug introduced in Chrome 91
// https://docs.google.com/document/d/16PFD1GtMI9Zgwu0jtPaKZJ75Q2wyZ9EZnVbBacOfiNA/edit#heading=h.irwtz58keq3
// https://bugs.chromium.org/p/chromium/issues/detail?id=1216197
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: ${Primary.navy[100]};
  background-color: ${Primary.neutral.white};
  height: 1px;
`

interface TableHeaderProps
  extends React.DetailedHTMLProps<
    React.ThHTMLAttributes<HTMLTableHeaderCellElement>,
    HTMLTableHeaderCellElement
  > {
  sortType?: Sort
  withoutBorder?: boolean
  onSort?: (sortType: Sort) => void
}

const TableHeaderCellWithRef = (
  { children, onSort = () => {}, sortType, ...props }: TableHeaderProps,
  ref: React.Ref<HTMLTableHeaderCellElement>
) => {
  const isAscSort = sortType === Sort.ASC
  const isDescSort = sortType === Sort.DESC

  return (
    <TableHeaderContent {...props}>
      <div
        ref={ref}
        css={css`
          display: flex;
          justify-content: flex-start;
          align-items: center;
          position: relative;
          padding-right: ${!!sortType && '25px'};
          & > label {
            padding: 0;
          }
        `}
      >
        {children}
        {!!sortType && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              position: absolute;
              right: 0;
            `}
          >
            <CaretSortUpIcon
              viewBox="0 -10 20 20"
              onClick={() =>
                isAscSort ? onSort(Sort.DEFAULT) : onSort(Sort.ASC)
              }
              css={css`
                cursor: pointer;
                &:hover * {
                  fill: ${isAscSort ? Primary.orange[100] : Primary.navy[40]};
                }
                & * {
                  fill: ${isAscSort ? Primary.orange[100] : Primary.navy[20]};
                }
              `}
              data-e2e="sort-asc"
            />
            <CaretSortDownIcon
              viewBox="0 10 20 20"
              onClick={() =>
                isDescSort ? onSort(Sort.DEFAULT) : onSort(Sort.DESC)
              }
              css={css`
                cursor: pointer;
                &:hover * {
                  fill: ${isDescSort ? Primary.orange[100] : Primary.navy[40]};
                }
                & * {
                  fill: ${isDescSort ? Primary.orange[100] : Primary.navy[20]};
                }
              `}
              data-e2e="sort-desc"
            />
          </div>
        )}
      </div>
    </TableHeaderContent>
  )
}

export const TableHeaderCell = React.memo(
  React.forwardRef(TableHeaderCellWithRef)
)

export const ExpandableSection: React.FC<AccordionProps> = (props) => (
  <Accordion {...props} />
)

export const ExpandableRowWithToggle: React.FC<
  React.HTMLAttributes<HTMLTableRowElement>
> = ({ children, ...props }) => {
  const { disabled, isOpen } = React.useContext(AccordionContext)
  return (
    <TableRow disabled={disabled} {...props}>
      <TableCell
        css={css`
          padding: 0;
          ${isOpen && 'box-shadow: inset 0 -1px 0px white;'}
          & > button {
            background: initial;
          }
          & > button:hover {
            background: initial;
          }
        `}
      >
        <AccordionHeader />
      </TableCell>
      {children}
    </TableRow>
  )
}

export const ExpandableDetailsRow: React.FC<
  React.HTMLAttributes<HTMLTableRowElement> & {
    colspan: number
  }
> = ({ children, colspan = 1, ...props }) => (
  <TableRow
    {...props}
    css={css`
      height: auto;
      &:hover {
        background-color: inherit !important;
      }
    `}
  >
    <TableCell />
    <TableCell colSpan={colspan}>
      <AccordionDetails
        css={css`
          border: none;
          padding: 0;
          background-color: inherit;
          & > * {
            ${mixins.menu}
            padding: 12px 0;
          }
        `}
      >
        <div>{children}</div>
      </AccordionDetails>
    </TableCell>
  </TableRow>
)
